import { useEffect, useMemo, useState } from "react";
import { useApolloClient, useQuery } from "@apollo/client/react";
import { useGlobalSearch } from "@/lib/use-global-search";
import { AgentRowsDocument, SearchEntityType } from "@/graphql/generated/operations";
import type { AgentListRow } from "./agents";

// Agents at instance scale (about 1000): never the whole list. The ranked ids come from
// `globalSearch` over AGENT, paged by skip/take (a blank query lists by label), and only the ids on
// screen are resolved to rows. The same two-phase shape as the person picker
// (lib/use-global-person-search.ts): search, then hydrate the current id set in rank order.

/** genesis refuses an `agents` page above this size. */
export const AGENT_PAGE_MAX = 100;

function chunk<T>(items: readonly T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

/**
 * The agent rows for these ids, keyed by id: `agents(where: {id: {in}})`, at most 100 ids a
 * request. The first 100 go through one watched query, which also follows later cache writes (an
 * admin's rename); any further ids go out in batches of 100 from an effect. No request for no ids.
 * While a new id set loads, the previous answer stays on screen, so a growing page never collapses
 * to empty.
 *
 * The single seam between the agent UI and genesis's row query: the picker's page, the stored
 * agents of a match and the PDF all go through here.
 */
export function useAgentsByIds(ids: readonly string[], { skip = false }: { skip?: boolean } = {}) {
  const client = useApolloClient();
  const unique = useMemo(() => [...new Set(ids)], [ids]);
  const head = useMemo(() => unique.slice(0, AGENT_PAGE_MAX), [unique]);
  const tail = useMemo(() => unique.slice(AGENT_PAGE_MAX), [unique]);
  const first = useQuery(AgentRowsDocument, {
    variables: { input: { first: AGENT_PAGE_MAX, where: { id: { in: head } } } },
    skip: skip || head.length === 0,
  });

  const tailKey = tail.join(",");
  const [more, setMore] = useState<{ key: string; nodes: AgentListRow[]; error?: Error }>({ key: "", nodes: [] });
  useEffect(() => {
    if (skip || tail.length === 0) return;
    let cancelled = false;
    Promise.all(
      chunk(tail, AGENT_PAGE_MAX).map((batch) =>
        client.query({
          query: AgentRowsDocument,
          variables: { input: { first: AGENT_PAGE_MAX, where: { id: { in: batch } } } },
        }),
      ),
    ).then(
      (results) => {
        if (!cancelled) setMore({ key: tailKey, nodes: results.flatMap((r) => r.data?.agents.nodes ?? []) });
      },
      (error: Error) => {
        if (!cancelled) setMore((prev) => ({ ...prev, key: tailKey, error }));
      },
    );
    return () => {
      cancelled = true;
    };
    // tailKey stands for `tail`: the effect re-runs when the id set changes, not on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, skip, tailKey]);

  const byId = useMemo(() => {
    const map = new Map<string, AgentListRow>();
    for (const node of first.data?.agents.nodes ?? first.previousData?.agents.nodes ?? []) map.set(node.id, node);
    for (const node of more.nodes) map.set(node.id, node);
    return map;
  }, [first.data, first.previousData, more.nodes]);
  const headAnswered = head.length === 0 || first.data != null;
  const tailAnswered = tail.length === 0 || (more.key === tailKey && !more.error);
  return {
    byId,
    // True once genesis has answered for this id set (or there was nothing to ask).
    ready: headAnswered && tailAnswered,
    loading: !(headAnswered && tailAnswered) && !(first.error ?? (more.key === tailKey ? more.error : undefined)),
    error: first.error ?? (more.key === tailKey ? more.error : undefined),
  };
}

function dedupe(ids: readonly string[]): string[] {
  return [...new Set(ids)];
}

/**
 * One page-by-page agent search: `query` blank lists every agent by label, otherwise genesis ranks
 * by full text with a trigram fallback. Returns rows in rank order plus the person picker's paging
 * fields, so the list UI is the same.
 */
export function useAgentSearch({
  query,
  pageSize = 24,
  skip = false,
}: {
  query: string;
  pageSize?: number;
  skip?: boolean;
}) {
  const trimmed = query.trim();
  const input = useMemo(
    () => ({ query: trimmed.length > 0 ? trimmed : null, entityTypes: [SearchEntityType.Agent] }),
    [trimmed],
  );
  const search = useGlobalSearch({ input, pageSize, skip });
  const ids = useMemo(() => dedupe(search.results.map((r) => r.entityId)), [search.results]);
  const rows = useAgentsByIds(ids, { skip });
  const nodes = useMemo(
    () => ids.map((id) => rows.byId.get(id)).filter((agent): agent is AgentListRow => Boolean(agent)),
    [ids, rows.byId],
  );
  const firstPageLoading = search.loading && search.results.length === 0;
  return {
    nodes,
    // The search answered and its rows are resolved: `nodes` is the real answer, empty or not.
    ready: !skip && !search.loading && search.error == null && rows.ready,
    loading: firstPageLoading || (ids.length > 0 && rows.loading),
    loadingMore: search.loadingMore,
    hasMore: search.hasMore,
    loadMoreFailed: search.loadMoreFailed,
    error: search.error ?? rows.error,
    loadMore: search.loadMore,
  };
}

/** The first `AGENT_PAGE_MAX` agents by name, for the recommend hand-off and auto-record. */
export function useFirstAgents() {
  const { data, error } = useQuery(AgentRowsDocument, { variables: { input: { first: AGENT_PAGE_MAX } } });
  const nodes = useMemo(() => data?.agents.nodes ?? [], [data]);
  return { nodes, ready: data != null, error };
}
